import { useState, useMemo, useCallback } from 'react';
import moment from 'moment';
import classTypeData from '../../../../Config/classType.json';

/**
 * Helper to recursively find whether a node contains a given targetId
 */
export const nodeContainsId = (node, targetId) => {
    if (!node) return false;
    if (Number(node.id) === Number(targetId)) return true;
    if (Array.isArray(node.nodes)) {
        return node.nodes.some(child => nodeContainsId(child, targetId));
    }
    return false;
};

/**
 * Helper to extract breadcrumb path list from indices tree
 */
export const getPathList = (nodes, currentId, depth = 0) => {
    if (!nodes || depth > 10) return [];
    for (let i = 0; i < nodes.length; i++) {
        const node = nodes[i];
        if (Number(node.id) === Number(currentId)) {
            return [node];
        }
        if (Array.isArray(node.nodes) && node.nodes.length > 0) {
            const sub = getPathList(node.nodes, currentId, depth + 1);
            if (sub.length > 0) {
                return [node, ...sub];
            }
        }
    }
    return [];
};

export const classTypeOptions = [
    { value: null, label: 'جميع الصفوف الدراسية' },
    ...(classTypeData?.ClassType || []).map(c => ({
        value: c.Id,
        label: c.Name
    }))
];

export function useUserMarksSearch(lessons = [], indices = null) {
    // 1. Search & Filter States
    const [searchTerm, setSearchTerm] = useState('');
    const [searchMode, setSearchMode] = useState('date'); // 'date' | 'all'
    const [groupLesson, setGroupLesson] = useState(true);
    const [startDate, setStartDate] = useState(moment().subtract(29, 'days').format('DD/MM/YYYY'));
    const [endDate, setEndDate] = useState(moment().format('DD/MM/YYYY'));
    const [dateString, setDateString] = useState(
        moment().subtract(29, 'days').format('DD/MM/YYYY') + " - " + moment().format('DD/MM/YYYY')
    );

    const [selectedClassTypeId, setSelectedClassTypeId] = useState(null);
    const [scoreFilter, setScoreFilter] = useState('ALL'); // ALL, EXCELLENT(>=90), VERY_GOOD(75-89), GOOD(60-74), NEEDS_WORK(<60)
    const [sortBy, setSortBy] = useState('DATE_DESC'); // DATE_DESC, DATE_ASC, SCORE_DESC, SCORE_ASC, ATTEMPTS_DESC, NAME_ASC
    const [quickTab, setQuickTab] = useState('ALL'); // ALL, MASTERED(>=80), PRACTICING(50-79), NEEDS_WORK(<50)
    const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);

    // 2. Date Apply Handler
    const handleApplyDate = useCallback((event, picker) => {
        const start = picker.startDate.format('DD/MM/YYYY');
        const end = picker.endDate.format('DD/MM/YYYY');
        const str = `${start} - ${end}`;
        picker.element.val(str);
        setStartDate(start);
        setEndDate(end);
        setDateString(str);
        setSearchMode('date');
    }, []);

    // Handle Setting "All Time"
    const handleSetAllTime = useCallback(() => {
        setSearchMode('all');
        setDateString('كل الفترات الزمنية');
    }, []);

    // 3. Statistics KPI Computation
    const stats = useMemo(() => {
        if (!Array.isArray(lessons) || lessons.length === 0) {
            return {
                totalLessons: 0,
                totalAttempts: 0,
                avgScoreRate: 0,
                bestScore: 0,
                masteredCount: 0,
                totalPracticeSeconds: 0,
                formattedTotalTime: '00:00'
            };
        }

        let totalAttemptsCount = 0;
        let scoreSum = 0;
        let validScoresCount = 0;
        let bestScorePct = 0;
        let masteredLessonsCount = 0;
        let totalSeconds = 0;

        lessons.forEach(item => {
            const attempts = Array.isArray(item.Attempts) && item.Attempts.length > 0
                ? item.Attempts
                : [{
                    Id: item.Id,
                    Mark: item.Mark,
                    TotalMark: item.TotalMark,
                    UserTime: item.UserTime,
                    CreationDate: item.CreationDate
                }];

            totalAttemptsCount += attempts.length;

            let lessonBestPct = 0;
            attempts.forEach(att => {
                const max = Number(att.TotalMark || item.TotalMark || 0);
                const mark = Number(att.Mark !== undefined && att.Mark !== null ? att.Mark : (item.Mark || 0));
                const pct = max > 0 ? Math.round((mark / max) * 100) : (mark > 0 ? Math.round(mark) : 0);

                if (att.Mark !== null && att.Mark !== undefined) {
                    scoreSum += pct;
                    validScoresCount++;
                }

                if (pct > lessonBestPct) {
                    lessonBestPct = pct;
                }

                // Time spent calculation
                const clock = att.UserTime?.Clock;
                if (clock) {
                    const h = Number(clock.hour || 0);
                    const m = Number(clock.minutes || 0);
                    const s = Number(clock.seconds || 0);
                    totalSeconds += (h * 3600) + (m * 60) + s;
                } else if (att.UserTime?.duration) {
                    totalSeconds += Number(att.UserTime.duration || 0);
                }
            });

            if (lessonBestPct > bestScorePct) {
                bestScorePct = lessonBestPct;
            }

            if (lessonBestPct >= 80) {
                masteredLessonsCount++;
            }
        });

        const avgScoreRate = validScoresCount > 0 ? Math.round(scoreSum / validScoresCount) : 0;

        // Format total practice time
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        const formattedTotalTime = hours > 0
            ? `${hours} س و ${minutes} د`
            : `${minutes} دقيقة`;

        return {
            totalLessons: lessons.length,
            totalAttempts: totalAttemptsCount,
            avgScoreRate,
            bestScore: bestScorePct,
            masteredCount: masteredLessonsCount,
            totalPracticeSeconds: totalSeconds,
            formattedTotalTime
        };
    }, [lessons]);

    // 4. Filter & Sort Logic
    const filteredLessons = useMemo(() => {
        if (!Array.isArray(lessons)) return [];

        let result = lessons.filter(lesson => {
            if (!lesson) return false;

            // 1. Text Search (Name, Index, Topic)
            if (searchTerm.trim()) {
                const query = searchTerm.trim().toLowerCase();
                const lessonName = (lesson.Name || lesson.title || '').toLowerCase();
                const indexName = (lesson.Index?.Name || '').toLowerCase();
                if (!lessonName.includes(query) && !indexName.includes(query)) {
                    return false;
                }
            }

            // 2. Class Type Filter (الصف الدراسي من 1 إلى 9)
            if (selectedClassTypeId) {
                const targetId = Number(selectedClassTypeId);
                const targetClassObj = (classTypeData?.ClassType || []).find(c => Number(c.Id) === targetId);
                const targetName = targetClassObj?.Name || '';

                const lessonClassId = Number(lesson.ClassTypeId || lesson.ClassType?.Id || lesson.ClassType || lesson.Class || lesson.Index?.ClassType || 0);
                let match = (lessonClassId === targetId);

                if (!match && targetName && indices?.nodes) {
                    const lessonIndexId = lesson.IndexId || lesson.Index?.Id;
                    const path = getPathList(indices.nodes, lessonIndexId);
                    match = path.some(p => (p.label || p.Name || '').includes(targetName) || Number(p.ClassType) === targetId) ||
                            (lesson.Name || '').includes(targetName) ||
                            (lesson.Index?.Name || '').includes(targetName);
                }

                if (!match) return false;
            }

            // Calculate primary score rate for filtering
            const attempts = Array.isArray(lesson.Attempts) && lesson.Attempts.length > 0
                ? lesson.Attempts
                : [{
                    Id: lesson.Id,
                    Mark: lesson.Mark,
                    TotalMark: lesson.TotalMark,
                    UserTime: lesson.UserTime,
                    CreationDate: lesson.CreationDate
                }];

            const primaryAttempt = attempts[0] || {};
            const maxMark = Number(primaryAttempt.TotalMark || lesson.TotalMark || 0);
            const userMark = Number(primaryAttempt.Mark !== undefined && primaryAttempt.Mark !== null ? primaryAttempt.Mark : (lesson.Mark || 0));
            const primaryScorePct = maxMark > 0 ? Math.round((userMark / maxMark) * 100) : (userMark > 0 ? Math.round(userMark) : 0);

            // Best score across all attempts
            let bestPct = primaryScorePct;
            attempts.forEach(a => {
                const m = Number(a.TotalMark || 0);
                const u = Number(a.Mark || 0);
                const p = m > 0 ? Math.round((u / m) * 100) : 0;
                if (p > bestPct) bestPct = p;
            });

            // 3. Quick Tabs Filter
            if (quickTab === 'MASTERED' && bestPct < 80) return false;
            if (quickTab === 'PRACTICING' && (bestPct < 50 || bestPct >= 80)) return false;
            if (quickTab === 'NEEDS_WORK' && bestPct >= 50) return false;

            // 4. Performance / Score Filter
            if (scoreFilter === 'EXCELLENT' && bestPct < 90) return false;
            if (scoreFilter === 'VERY_GOOD' && (bestPct < 75 || bestPct >= 90)) return false;
            if (scoreFilter === 'GOOD' && (bestPct < 60 || bestPct >= 75)) return false;
            if (scoreFilter === 'NEEDS_WORK' && bestPct >= 60) return false;

            return true;
        });

        // 5. Sorting
        result.sort((a, b) => {
            if (sortBy === 'NAME_ASC') {
                const nameA = (a.Name || a.title || '').trim();
                const nameB = (b.Name || b.title || '').trim();
                return nameA.localeCompare(nameB, 'ar');
            }

            if (sortBy === 'ATTEMPTS_DESC') {
                const countA = Array.isArray(a.Attempts) ? a.Attempts.length : 1;
                const countB = Array.isArray(b.Attempts) ? b.Attempts.length : 1;
                return countB - countA;
            }

            const getBestScore = (item) => {
                const atts = Array.isArray(item.Attempts) && item.Attempts.length > 0 ? item.Attempts : [item];
                let best = 0;
                atts.forEach(at => {
                    const max = Number(at.TotalMark || item.TotalMark || 0);
                    const mark = Number(at.Mark || 0);
                    const pct = max > 0 ? Math.round((mark / max) * 100) : (mark > 0 ? Math.round(mark) : 0);
                    if (pct > best) best = pct;
                });
                return best;
            };

            if (sortBy === 'SCORE_DESC') {
                return getBestScore(b) - getBestScore(a);
            }

            if (sortBy === 'SCORE_ASC') {
                return getBestScore(a) - getBestScore(b);
            }

            if (sortBy === 'DATE_ASC') {
                const dateA = a.CreationDate || a.Attempts?.[0]?.CreationDate || 0;
                const dateB = b.CreationDate || b.Attempts?.[0]?.CreationDate || 0;
                return dateA > dateB ? 1 : -1;
            }

            // Default: DATE_DESC
            const dateA = a.CreationDate || a.Attempts?.[0]?.CreationDate || 0;
            const dateB = b.CreationDate || b.Attempts?.[0]?.CreationDate || 0;
            return dateA < dateB ? 1 : -1;
        });

        return result;
    }, [lessons, searchTerm, selectedClassTypeId, indices, quickTab, scoreFilter, sortBy]);

    // Active Filters Check
    const hasActiveFilters = Boolean(
        searchTerm ||
        selectedClassTypeId !== null ||
        scoreFilter !== 'ALL' ||
        quickTab !== 'ALL' ||
        sortBy !== 'DATE_DESC' ||
        searchMode === 'all'
    );

    // Reset All Filters Handler
    const handleResetFilters = useCallback(() => {
        setSearchTerm('');
        setSelectedClassTypeId(null);
        setScoreFilter('ALL');
        setQuickTab('ALL');
        setSortBy('DATE_DESC');
        setSearchMode('date');
        setGroupLesson(true);
        const start = moment().subtract(29, 'days').format('DD/MM/YYYY');
        const end = moment().format('DD/MM/YYYY');
        setStartDate(start);
        setEndDate(end);
        setDateString(`${start} - ${end}`);
    }, []);

    return {
        searchTerm,
        setSearchTerm,
        searchMode,
        setSearchMode,
        groupLesson,
        setGroupLesson,
        startDate,
        endDate,
        dateString,
        handleApplyDate,
        handleSetAllTime,
        selectedClassTypeId,
        setSelectedClassTypeId,
        classTypeOptions,
        scoreFilter,
        setScoreFilter,
        sortBy,
        setSortBy,
        quickTab,
        setQuickTab,
        showAdvancedSearch,
        setShowAdvancedSearch,
        stats,
        filteredLessons,
        hasActiveFilters,
        handleResetFilters
    };
}
